import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { IS_PRODUCTION, PlatformService, RmaUrlsService } from '@rma/generic/util/environment';
import { filter, map, mergeMap } from 'rxjs/operators';
import { BaseMetaData, LinkingData, LinkingDataTypes, MetaInfo, MetaNoIndex, MetaTag, SocialTagConfig } from '../domain';
import { LinkingDataService } from '../util-linking-data/linking-data.service';
import { MetaTagService } from '../util-meta-tag-service/meta-tag.service';

@Injectable({
  providedIn: 'root',
})
export class MetaService {
  public readonly defaultImage: string;
  public readonly defaultOgImage: string;

  constructor(
    @Inject(DOCUMENT) private readonly dom: Document,
    private readonly rmaUrls: RmaUrlsService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly linkingDataService: LinkingDataService,
    private readonly meta: Meta,
    private readonly metaTagService: MetaTagService,
    private readonly platformService: PlatformService,
    private readonly router: Router,
    private readonly title: Title,
  ) {
    this.defaultImage = this.rmaUrls.cdnUrl('suburb-default.jpg');
    this.defaultOgImage = this.rmaUrls.imageUrl(`opengraph/og_img_2.jpg`);
  }

  public update(
    baseMetaData: BaseMetaData | null = null,
    linkingData: LinkingData | null = null,
    socialConfig: Partial<SocialTagConfig> | null = null,
  ): void {
    if (baseMetaData === null) {
      this.pushNoIndex();
      return;
    }

    // We only care about the extended tags on the server, not in the browser
    if (this.platformService.isPlatformServer || !IS_PRODUCTION) {
      this.updateAllTags(baseMetaData, socialConfig, linkingData);
    } else {
      this.updateTitle(baseMetaData.title, baseMetaData.hideBrand);
    }
  }

  public noIndex(metaNoIndex?: MetaNoIndex) {
    this.pushNoIndex();

    if (metaNoIndex?.title) {
      this.updateTitle(metaNoIndex.title, metaNoIndex.hideBrand);
    }
  }

  public watchRouting(): void {
    // is this neccessary?
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
      )
      .subscribe(({ title, description }) => {
        if (title) {
          this.updateTitle(title);
        }
        if (description) {
          this.updateMetaTag({ content: description, name: 'description' });
        }
      });
  }

  private updateAllTags(baseMetaData: BaseMetaData, social: Partial<SocialTagConfig> | null, linkingData: Partial<LinkingData> | null) {
    const { author, keywords, nextUrl, prevUrl, description, title } = baseMetaData;
    const canonicalUrl = this.getCanonicalUrl(baseMetaData.canonicalUrl);
    const image = baseMetaData.imageUrl ?? this.defaultOgImage;

    const socialData = { author, description, image, keywords, title, url: canonicalUrl };
    const metaTags = this.metaTagService.generateMetaTags(socialData, social ?? {});
    const ldSchema = this.linkingDataService.generateLdSchema(linkingData);
    const metaInfo = { canonicalUrl, ldSchema, metaTags, nextUrl, title, previousUrl: prevUrl };

    this.pushInfo(metaInfo, baseMetaData.hideBrand);
  }

  private pushInfo(metaInfo: MetaInfo, hideBrand = true): void {
    this.updateTitle(metaInfo.title, hideBrand);
    this.updateMetaTags(metaInfo.metaTags ?? []);
    this.updateCanonicalURL(metaInfo.canonicalUrl);
    this.updateNextOrPreviousURL(metaInfo.nextUrl ?? '', 'next');
    this.updateNextOrPreviousURL(metaInfo.previousUrl ?? '', 'previous');

    if (metaInfo.ldSchema) {
      this.updateLinkingData(metaInfo.ldSchema);
    }
  }

  private pushNoIndex(): void {
    this.updateMetaTag({ content: 'noindex', name: 'robots' });
  }

  private getCanonicalUrl(canonicalUrl?: string) {
    if (canonicalUrl) {
      return (canonicalUrl.startsWith('http') ? canonicalUrl : this.rmaUrls.baseUrl(canonicalUrl)).split(/[?#]/)[0];
    }

    return this.dom.URL.replace('http://', 'https://').split(/[?#]/)[0];
  }

  private updateTitle(baseTitle: string, hideBrand = true): void {
    const title = `${baseTitle}${hideBrand ? '' : ' | RateMyAgent'}`;
    this.title.setTitle(title);
  }

  private updateMetaTags(metaTags: MetaTag[]): void {
    const tags = metaTags.filter(({ content }) => Boolean(content));
    this.meta.addTags(tags, true);
  }

  private updateMetaTag(metaTag: MetaTag): void {
    if (!metaTag.content) {
      return;
    }
    this.meta.updateTag({ ...metaTag });
  }

  private updateCanonicalURL(url?: string): void {
    const canUrl = url ? url : this.dom.URL;
    const selector = "link[rel='canonical']";
    const rel = 'canonical';

    this.updateHeadLink(selector, rel, canUrl);
  }

  private updateNextOrPreviousURL(url: string, nextOrPrevious: string): void {
    const selector = `link[rel='${nextOrPrevious}']`;
    const rel = nextOrPrevious;

    this.updateHeadLink(selector, rel, url);
  }

  private updateHeadLink(selector: string, rel: string, url?: string): void {
    const existingLink = this.dom.querySelector(selector);

    if (!url) {
      if (existingLink) {
        existingLink.remove();
      }
      return;
    }

    if (existingLink) {
      existingLink.setAttribute('href', url);
    } else {
      const link: HTMLLinkElement = this.dom.createElement('link');
      link?.setAttribute('rel', rel);
      link?.setAttribute('href', url);
      this.dom.head?.appendChild(link);
    }
  }

  private updateLinkingData(schema?: LinkingDataTypes[]) {
    const existingTag = this.dom.querySelector("script[type='application/ld+json']");
    if (existingTag) {
      existingTag.remove();
    }
    if (schema) {
      // safari 16.5+ has issues with passing @context in arrays. We probably need to add a higher level @context and @type
      if (Array.isArray(schema)) {
        schema.forEach((data) => this.addLinkingDataToDom(data));
      } else {
        this.addLinkingDataToDom(schema);
      }
    }
  }

  private addLinkingDataToDom(data: LinkingDataTypes) {
    if (data) {
      const ldJsonScript = this.dom.createElement('script');
      ldJsonScript.setAttribute('type', 'application/ld+json');
      ldJsonScript.innerHTML = JSON.stringify(data, null, 1);
      this.dom.head?.appendChild(ldJsonScript);
    }
  }
}
